import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import { Order } from "@/lib/types";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, StatusBadge, EmptyState } from "@/components/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const CHANNELS = ["all", "Shopify", "Amazon", "Mercado Libre"];
const RANGES: Record<string, number | null> = { all: null, "7": 7, "30": 30, "90": 90 };
const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const lastMonths = () =>
  Array.from({ length: 6 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    return { key: `m:${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}` };
  });

const OrdersSummary: React.FC<{ orders: Order[] }> = ({ orders }) => {
  const total = orders.reduce((s, o) => s + o.total, 0);
  const paid = orders.filter((o) => o.financial_status === "cobrado").reduce((s, o) => s + o.total, 0);
  return (
    <div className="-mt-4 mb-4 flex justify-end gap-4 text-xs text-slate-500" data-testid="orders-summary">
      <span>{orders.length} órdenes</span>
      <span>Total: <span className="font-medium tabular text-slate-900" data-testid="orders-summary-total">{money(total)}</span></span>
      <span>Cobrado: <span className="font-medium tabular text-emerald-600" data-testid="orders-summary-paid">{money(paid)}</span></span>
    </div>
  );
};

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("all");
  const [range, setRange] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    const params: any = {};
    if (channel !== "all") params.channel = channel;
    const days = RANGES[range];
    if (range.startsWith("m:")) {
      const m = range.slice(2);
      const last = new Date(parseInt(m.slice(0, 4), 10), parseInt(m.slice(5, 7), 10), 0).getDate();
      params.date_from = `${m}-01`;
      params.date_to = `${m}-${last}`;
    } else if (days) {
      const d = new Date();
      d.setDate(d.getDate() - days);
      params.date_from = d.toISOString().slice(0, 10);
    }
    const res = await client.get("/orders", { params });
    setOrders(res.data);
    setLoading(false);
  }, [channel, range]);

  useEffect(() => {
    load();
  }, [load]);

  const total = orders.reduce((s, o) => s + o.total, 0);

  return (
    <div data-testid="orders-page">
      <PageHeader
        title="Órdenes"
        subtitle={`${orders.length} órdenes · ${money(total)} vendido`}
        actions={
          <div className="flex gap-2">
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="w-44 rounded-none border-slate-300" data-testid="orders-channel-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHANNELS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c === "all" ? "Todos los canales" : c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-40 rounded-none border-slate-300" data-testid="orders-date-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todo el periodo</SelectItem>
                <SelectItem value="7">Últimos 7 días</SelectItem>
                <SelectItem value="30">Últimos 30 días</SelectItem>
                <SelectItem value="90">Últimos 90 días</SelectItem>
                {lastMonths().map((m) => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {!loading && orders.length > 0 && <OrdersSummary orders={orders} />}

      <div className="border border-slate-200 bg-white">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#DD0000]" />
          </div>
        ) : orders.length === 0 ? (
          <EmptyState message="No hay órdenes para los filtros seleccionados" testid="orders-empty" />
        ) : (
          <table className="w-full text-sm" data-testid="orders-table">
            <thead>
              <tr className="bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Orden</th>
                <th className="px-5 py-3">Canal</th>
                <th className="px-5 py-3">Cliente</th>
                <th className="px-5 py-3">Producto</th>
                <th className="px-5 py-3">Fecha</th>
                <th className="px-5 py-3">Estado</th>
                <th className="px-5 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`order-row-${o.id}`}>
                  <td className="px-5 py-3 font-mono text-xs text-slate-700">{o.order_number}</td>
                  <td className="px-5 py-3 text-slate-600">{o.channel}</td>
                  <td className="px-5 py-3 text-slate-700">{o.customer}</td>
                  <td className="px-5 py-3 text-slate-500">{o.product}</td>
                  <td className="px-5 py-3 text-slate-500">{fmtDate(o.created_at)}</td>
                  <td className="px-5 py-3">
                    <StatusBadge
                      status={o.financial_status}
                      label={o.financial_status === "cobrado" ? "Cobrado" : "Pendiente"}
                    />
                  </td>
                  <td className="px-5 py-3 text-right font-medium tabular text-slate-900">{money(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
